// PanelCanvas2D.tsx — 2D engineering drawing (elevation / face-view) of a tilt-up panel
// Renders as an SVG resembling a real tilt-up shop drawing:
//   - Panel outline with concrete fill
//   - Openings: hatched white cutouts with (mark) tag and W×H size label
//   - Feature strips: horizontal/vertical banded hatch
//   - Pilasters: widened side rectangles
//   - Roof line: dashed horizontal line with height annotation
//   - Top plate: solid band at panel top (when enabled)
//   - Weld connections:  ⊠ gold square symbol + mark tag
//   - Brace points:      ⊕ orange circle symbol + mark tag
//   - Pick points:       △ green triangle symbol + mark tag
//   - CG marker: blue crosshair circle with centroid coords label
//   - Horizontal dim chain (above panel): segment widths + overall
//   - Vertical dim chain (right of panel): segment heights + overall + roof line

import React, { useMemo, useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import type { PanelData } from '../../types/panelvar';
import { useShallowStore } from '../../store/useStore';
import { isVisibleForFilter } from '../../utils/entityFace';
import type { EntityFace } from '../../utils/entityFace';
import { MOUNT_TYPES, MOUNT_TYPE_LABELS, MOUNT_TYPE_COLORS } from '../../types/mountType';
import { calcMaterials, computeArchScale, PAPER_SHEET_DIMS } from '../../utils/materialsCalc';
import { outlineFromMp, makeSVGPath } from '../../utils/panelGeometry';
import { detectPanelClashes, getClashingEntities } from '../../utils/clashDetection';
import { computeEdgeTrims } from '../../utils/buildingFootprint';
import { HorizDim, VertDim, WeldSymbol, BraceSymbol, PickSymbol, CGMarker } from './canvasSymbols';
import {
  SNAP, SNAP_FINE, HANDLE_SZ,
  toFtIn, rectsOverlap,
  applyMove, applyResize, getResizeHandles,
} from './panelCanvasGeom';
import type { DragState, SelBounds } from './panelCanvasGeom';

// ── Layout margins (SVG pixels) ───────────────────────────────────────────────
const ML = 72;   // left  — space for extension lines
const MR = 230;  // right — vertical dim chain columns + labels
const MT = 104;  // top   — horizontal dim chain rows
const MB = 220;  // bottom — title block table

// ── Base drawing scale: SVG px per panel inch (fixed — never adapts to container) ──
// Zooming is done by physically resizing the page frame, not by recomputing this.
const BASE_SCALE = 2.0;

// ── Base paper resolution: display px per paper-inch at pageZoom=1 ───────────
// ARCH-D portrait (24"×36") → 480×720px at zoom=1; users zoom in/out from there.
const BASE_PAPER_PX = 20;

// ── Dimension chain row/col spacing ──────────────────────────────────────────
const DS = 32; // pixels between stacked dim rows/columns

// ── Pattern IDs ──────────────────────────────────────────────────────────────
const HATCH_DIAG  = 'cv2d-hatch-diag';
const HATCH_HORIZ = 'cv2d-hatch-horiz';

// ── Font for tag/title-block labels ───────────────────────────────────────────
const FONT_TAG = "'Courier New',monospace";

// ── Opening data (normalised to center-x, center-y) ───────────────────────────
interface ORec { mark: string; x: number; y: number; w: number; h: number; label: string; entityId: string }
interface OCirc { mark: string; x: number; y: number; r: number; entityId: string }

// ── Main component ────────────────────────────────────────────────────────────
interface Props { panel: PanelData }

export default function PanelCanvas2D({ panel }: Props) {

  // ── Store subscriptions ────────────────────────────────────────────────────
  const { selectedEntities, selectEntity, setSelection, clearSelection, updatePanel, updateMpVar, deleteSelectedEntities, setFocusField,
    startPlacement, showContextMenu, editEntity, faceFilter, setFaceFilter, layerVisibility, toggleLayerVisibility, project } =
    useShallowStore(s => ({
      selectedEntities:       s.ui.selectedEntities,
      selectEntity:           s.selectEntity,
      setSelection:           s.setSelection,
      clearSelection:         s.clearSelection,
      updatePanel:            s.updatePanel,
      updateMpVar:            s.updateMpVar,
      deleteSelectedEntities: s.deleteSelectedEntities,
      setFocusField:          s.setFocusField,
      startPlacement:         s.startPlacement,
      showContextMenu:        s.showContextMenu,
      editEntity:             s.editEntity,
      faceFilter:             s.ui.faceFilter,
      setFaceFilter:          s.setFaceFilter,
      layerVisibility:        s.ui.layerVisibility,
      toggleLayerVisibility:  s.toggleLayerVisibility,
      project:                s.project,
    }));
  const selectedId = selectedEntities[0] ?? null;

  // ── Drag refs & state ──────────────────────────────────────────────────────
  const svgRef          = useRef<SVGSVGElement>(null);
  const dragRef         = useRef<DragState | null>(null);
  const currentDeltaRef = useRef<{ dpx: number; dpy: number } | null>(null);
  const [dragDelta, setDragDelta] = useState<{ dpx: number; dpy: number } | null>(null);

  // ── Middle-mouse pan drag ref ──────────────────────────────────────────────
  // ── Scroll container + page-level zoom
  const containerRef     = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef<{ scrollLeft: number; scrollTop: number } | null>(null);
  const [pageZoom, setPageZoom] = useState(1.0);

  // ── Rubberband multi-select state ──────────────────────────────────────────
  const [rbStart, setRbStart] = useState<{ x: number; y: number } | null>(null);
  const [rbEnd, setRbEnd]     = useState<{ x: number; y: number } | null>(null);

  const mp = panel.mpvar;
  const PW = mp.width  > 0 ? mp.width  : 360;
  const PH = mp.height > 0 ? mp.height : 336;

  // FFE datum — distance from panel bottom to Finished Floor Elevation
  const ffeY = -(mp.botElev ?? 0) * 12;

  // ── Scale: fixed drawing resolution (px per panel inch) ───────────────────
  // Zoom is handled via SVG viewBox — BASE_SCALE never changes with container.
  const scale = BASE_SCALE;

  // ── SVG coordinate helpers ─────────────────────────────────────────────────
  const sx  = (px: number) => ML + px * scale;
  const sy  = (py: number) => MT + (PH - py) * scale;   // Y-up → Y-down
  const syE = (ey: number) => sy(ey + ffeY);             // FFE-relative → SVG

  const svgW = ML + PW * scale + MR;
  const svgH = MT + PH * scale + MB;
  // ── Paper sheet display dimensions (independent of panel content) ─────────
  const paperSize    = project?.settings.paperSize ?? 'ARCH-D';
  const orientation  = project?.settings.paperOrientation ?? 'portrait';
  const [_dw, _dh]   = PAPER_SHEET_DIMS[paperSize] ?? [36, 24]; // stored as landscape [w,h]
  const [papW, papH] = orientation === 'portrait' ? [_dh, _dw] : [_dw, _dh];
  const pagePxW      = papW * BASE_PAPER_PX; // px at pageZoom=1
  const pagePxH      = papH * BASE_PAPER_PX;
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const availH = container.clientHeight - 48;
    const availW = container.clientWidth  - 48;
    if (availH <= 0 || availW <= 0) return;
    setPageZoom(Math.max(0.05, Math.min(availH / pagePxH, availW / pagePxW)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Apply cursor-anchored scroll after zoom renders to DOM ────────────────
  useLayoutEffect(() => {
    if (!pendingScrollRef.current || !containerRef.current) return;
    containerRef.current.scrollLeft = pendingScrollRef.current.scrollLeft;
    containerRef.current.scrollTop  = pendingScrollRef.current.scrollTop;
    pendingScrollRef.current = null;
  }, [pageZoom]);

  // Precomputed panel-corner SVG coords
  const panLeft  = ML;
  const panRight = ML + PW * scale;
  const panTop   = MT;                      // sy(PH)
  const panBot   = MT + PH * scale;        // sy(0)

  // ── Panel outline polygon (actual top shape including angles) ──────────────
  const panelOutline = useMemo(() => outlineFromMp(mp), [mp.width, mp.height, mp.heightL, mp.heightR, mp.topAngleL, mp.topAngleR, mp.topRadiusL, mp.topRadiusR]);
  const outlinePath  = useMemo(() => makeSVGPath(panelOutline, scale, panLeft, panBot), [panelOutline, scale, panLeft, panBot]);

  // ── Rectangular openings (normalised to centre X/Y) ────────────────────────
  const { rects, circs } = useMemo(() => {
    const rects: ORec[] = [];
    const circs: OCirc[] = [];
    panel.rovar.forEach((s, i) => {
      if (!s.enabled || !s.data.width || !s.data.height) return;
      const d = s.data;
      rects.push({ mark: d.mark, x: d.x, y: d.y + ffeY, w: d.width, h: d.height, label: 'RO', entityId: `rovar-${i}` });
    });
    panel.wdvar.forEach((s, i) => {
      if (!s.enabled || !s.data.width || !s.data.height) return;
      const d = s.data;
      rects.push({ mark: d.mark, x: d.x, y: d.y + ffeY, w: d.width, h: d.height, label: 'WD', entityId: `wdvar-${i}` });
    });
    panel.drvar.forEach((s, i) => {
      if (!s.enabled || !s.data.width || !s.data.height) return;
      const d = s.data;
      // drvar.y = sill elev from FFE → centre Y = y + ffeY + height/2
      rects.push({ mark: d.mark, x: d.x, y: d.y + ffeY + d.height / 2, w: d.width, h: d.height, label: 'DR', entityId: `drvar-${i}` });
    });
    panel.dlvar.forEach((s, i) => {
      if (!s.enabled || !s.data.width || !s.data.height) return;
      const d = s.data;
      rects.push({ mark: d.mark, x: d.x, y: d.y + ffeY, w: d.width, h: d.height, label: 'DL', entityId: `dlvar-${i}` });
    });
    panel.sbvar.forEach((s, i) => {
      if (!s.enabled || !s.data.width || !s.data.height) return;
      const d = s.data;
      rects.push({ mark: d.mark, x: d.x, y: d.y + ffeY, w: d.width, h: d.height, label: 'BO', entityId: `sbvar-${i}` });
    });
    panel.rbvar.forEach((s, i) => {
      if (!s.enabled || !s.data.diameter) return;
      const d = s.data;
      circs.push({ mark: d.mark, x: d.x, y: d.y + ffeY, r: d.diameter / 2, entityId: `rbvar-${i}` });
    });
    return { rects, circs };
  }, [panel, ffeY]);

  // ── Per-panel clash detection (feature-vs-feature + feature-vs-edge) ───────
  const panelClashes = useMemo(() => detectPanelClashes(panel), [panel]);
  const { entityIds: clashEntityIds, edges: clashEdges } = useMemo(() => getClashingEntities(panel), [panel]);

  // ── Joint gap trims ────────────────────────────────────────────────────────
  const jointTrims = useMemo(() => {
    if (!project) return { leftTrim: 0, rightTrim: 0 };
    const elev = project.elevations.find(e => e.panelIds.includes(panel.id));
    if (!elev) return { leftTrim: 0, rightTrim: 0 };
    return computeEdgeTrims(panel.id, elev, project.settings.jointWidth);
  }, [panel.id, project]);

  // ── Centre of Gravity ─────────────────────────────────────────────────────
  const { cgX, cgY } = useMemo(() => {
    const grossA = PW * PH;
    let opA = 0, opXM = 0, opYM = 0;
    rects.forEach(o => { const a = o.w * o.h; opA += a; opXM += a * o.x; opYM += a * o.y; });
    circs.forEach(o => { const a = Math.PI * o.r * o.r; opA += a; opXM += a * o.x; opYM += a * o.y; });
    const netA = grossA - opA;
    if (netA <= 0) return { cgX: PW / 2, cgY: PH / 2 };
    return { cgX: (grossA * (PW / 2) - opXM) / netA, cgY: (grossA * (PH / 2) - opYM) / netA };
  }, [PW, PH, rects, circs]);

  // ── Horizontal dim chain breaks (panel X positions) ───────────────────────
  const horizBreaks = useMemo<number[]>(() => {
    const xs = new Set<number>([0, PW]);
    rects.forEach(o => {
      const l = Math.max(0, o.x - o.w / 2);
      const r = Math.min(PW, o.x + o.w / 2);
      xs.add(l); xs.add(r);
    });
    return [...xs].sort((a, b) => a - b);
  }, [PW, rects]);

  // ── Vertical dim chain breaks (panel Y positions) ─────────────────────────
  const vertBreaks = useMemo<number[]>(() => {
    const ys = new Set<number>([0, PH]);
    rects.forEach(o => {
      ys.add(Math.max(0, o.y - o.h / 2));
      ys.add(Math.min(PH, o.y + o.h / 2));
    });
    const rl = mp.roofLine;
    if (typeof rl === 'number' && rl + ffeY > 0 && rl + ffeY < PH) ys.add(rl + ffeY);
    return [...ys].sort((a, b) => a - b);
  }, [PH, rects, mp.roofLine, ffeY]);

  // ── Dim row/col SVG positions ─────────────────────────────────────────────
  const dimRow1 = panTop - DS;          // innermost horizontal row (segments)
  const dimRow2 = panTop - DS * 2;      // outer horizontal row (overall width)
  const dimCol1 = panRight + DS;        // innermost vertical col (segments)
  const dimCol2 = panRight + DS * 2;    // vertical col 2
  const dimCol3 = panRight + DS * 3;    // vertical col 3

  const roofLineY = (typeof mp.roofLine === 'number' && mp.roofLine + ffeY > 0 && mp.roofLine + ffeY < PH)
    ? mp.roofLine + ffeY : null;
  const roofLineYR = (mp.roofLineSloped && typeof mp.roofLineR === 'number' && mp.roofLineR + ffeY > 0 && mp.roofLineR + ffeY < PH)
    ? mp.roofLineR + ffeY : null;

  // ── Commit drag delta to store ─────────────────────────────────────────────

  /** Convert a client-space point to SVG drawing coordinates.
   *  Uses getScreenCTM().inverse() so the result correctly accounts for the
   *  current viewBox pan + zoom — no manual rect math needed. */
  function clientToSvg(clientX: number, clientY: number): { x: number; y: number } {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt  = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const svgPt = pt.matrixTransform(ctm.inverse());
    return { x: svgPt.x, y: svgPt.y };
  }

  function commitDrag(drag: DragState, delta: { dpx: number; dpy: number }) {    if (drag.action === 'move') {
      const res = applyMove(panel, drag.entityId, delta.dpx, delta.dpy, drag.fine);
      if (res) updatePanel(panel.id, { [res.slotKey]: res.newArr } as Partial<PanelData>);
    } else {
      const res = applyResize(panel, drag.entityId, drag.handle, delta.dpx, delta.dpy, drag.fine);
      if (res) {
        if ('isMpVar' in res) updateMpVar(panel.id, res.mpPatch);
        else updatePanel(panel.id, { [res.slotKey]: res.newArr } as Partial<PanelData>);
      }
    }
  }

  function onEntityMouseDown(e: React.MouseEvent, entityId: string) {
    if (e.button !== 0) return; // only left-click initiates entity drag
    e.stopPropagation();
    if (entityId === 'panel-body') return; // panel-body handled separately
    selectEntity(entityId);
    const svgPt = clientToSvg(e.clientX, e.clientY);
    dragRef.current = {
      entityId, action: 'move', handle: '',
      startSvgX: svgPt.x,
      startSvgY: svgPt.y,
      fine: e.shiftKey,
    };
  }

  /** Double-click entity → open edit dialog */
  function onEntityDblClick(e: React.MouseEvent, entityId: string) {
    e.stopPropagation();
    if (entityId === 'panel-body') return;
    editEntity(entityId);
  }

  /** Right-click entity → context menu */
  function onEntityContextMenu(e: React.MouseEvent, entityId: string) {
    e.preventDefault();
    e.stopPropagation();
    const { panelX, panelY } = svgToPanel(e);
    showContextMenu(entityId, e.clientX, e.clientY, panelX, panelY);
  }

  /** Convert SVG mouse event to panel coords (accounts for viewBox zoom/pan) */
  function svgToPanel(e: React.MouseEvent): { panelX: number; panelY: number } {
    const { x, y } = clientToSvg(e.clientX, e.clientY);
    return {
      panelX: (x - ML) / scale,
      panelY: PH - (y - MT) / scale,
    };
  }

  /** Double-click on panel body → placement menu */
  function onPanelBodyDblClick(e: React.MouseEvent) {
    e.stopPropagation();
    const { panelX, panelY } = svgToPanel(e);
    startPlacement(panelX, panelY, e.clientX, e.clientY);
  }

  /** Right-click on panel body → paste context menu */
  function onPanelBodyContextMenu(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const { panelX, panelY } = svgToPanel(e);
    showContextMenu(null, e.clientX, e.clientY, panelX, panelY);
  }

  // Long-press detection for touch devices on panel body
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTouch = useRef<{ x: number; y: number } | null>(null);

  function onPanelBodyTouchStart(e: React.TouchEvent) {
    const t = e.touches[0];
    longPressTouch.current = { x: t.clientX, y: t.clientY };
    longPressTimer.current = setTimeout(() => {
      if (!longPressTouch.current) return;
      const { x, y } = longPressTouch.current;
      const r = svgRef.current!.getBoundingClientRect();
      const panelX = (x - r.left - ML) / scale;
      const panelY = PH - (y - r.top - MT) / scale;
      startPlacement(panelX, panelY, x, y);
      longPressTouch.current = null;
    }, 500);
  }

  function onPanelBodyTouchMove(e: React.TouchEvent) {
    if (!longPressTouch.current || !longPressTimer.current) return;
    const t = e.touches[0];
    const dx = Math.abs(t.clientX - longPressTouch.current.x);
    const dy = Math.abs(t.clientY - longPressTouch.current.y);
    if (dx > 10 || dy > 10) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
      longPressTouch.current = null;
    }
  }

  function onPanelBodyTouchEnd() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    longPressTouch.current = null;
  }

  function onHandleMouseDown(e: React.MouseEvent, entityId: string, handle: string) {
    if (e.button !== 0) return; // only left-click initiates resize
    e.stopPropagation();
    const svgPt = clientToSvg(e.clientX, e.clientY);
    dragRef.current = {
      entityId, action: 'resize', handle,
      startSvgX: svgPt.x,
      startSvgY: svgPt.y,
      fine: e.shiftKey,
    };
  }

  function onSvgMouseMove(e: React.MouseEvent<SVGSVGElement>) {
    if (!dragRef.current) return;
    const { startSvgX, startSvgY } = dragRef.current;
    const cur = clientToSvg(e.clientX, e.clientY);
    const dpx =  (cur.x - startSvgX) / scale;
    const dpy = -(cur.y - startSvgY) / scale;
    currentDeltaRef.current = { dpx, dpy };
    setDragDelta({ dpx, dpy });
  }

  function onSvgMouseUp() {
    const drag = dragRef.current;
    const delta = currentDeltaRef.current;
    dragRef.current = null;
    currentDeltaRef.current = null;
    if (drag && delta && (Math.abs(delta.dpx) > 1 || Math.abs(delta.dpy) > 1)) {
      commitDrag(drag, delta);
    }
    setDragDelta(null);
  }


  // ── Scroll-wheel zoom (cursor-anchored) on the scroll container ───────────
  const onContainerWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const rect     = container.getBoundingClientRect();
    const factor   = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    // Content position under cursor before zoom (scroll-content space)
    const contentX  = e.clientX - rect.left + container.scrollLeft;
    const contentY  = e.clientY - rect.top  + container.scrollTop;
    // Cursor offset within the container viewport
    const viewportX = e.clientX - rect.left;
    const viewportY = e.clientY - rect.top;
    // After zoom, scroll so the same content point stays under the cursor
    pendingScrollRef.current = {
      scrollLeft: contentX * factor - viewportX,
      scrollTop:  contentY * factor - viewportY,
    };
    setPageZoom(prev => Math.max(0.05, Math.min(prev * factor, 10)));
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('wheel', onContainerWheel, { passive: false });
    return () => container.removeEventListener('wheel', onContainerWheel);
  }, [onContainerWheel]);

  // ── Fit + step-zoom helpers ────────────────────────────────────────────────
  const fitView = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const availH = container.clientHeight - 48;
    const availW = container.clientWidth  - 48;
    if (availH <= 0 || availW <= 0) return;
    setPageZoom(Math.max(0.05, Math.min(availH / pagePxH, availW / pagePxW)));
  }, [pagePxW, pagePxH]);

  const zoomStep = useCallback((factor: number) => {
    setPageZoom(prev => Math.max(0.05, Math.min(prev * factor, 10)));
  }, []);

  function onKeyDown(e: React.KeyboardEvent) {    if (e.key === 'Escape') {
      if (rbStart) { setRbStart(null); setRbEnd(null); return; }
      clearSelection();
      return;
    }
    if (!selectedId) return;
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      deleteSelectedEntities();
      return;
    }
    const step = e.shiftKey ? SNAP_FINE : SNAP;
    let dpx = 0, dpy = 0;
    if (e.key === 'ArrowLeft')  dpx = -step;
    if (e.key === 'ArrowRight') dpx = +step;
    if (e.key === 'ArrowUp')    dpy = +step;
    if (e.key === 'ArrowDown')  dpy = -step;
    if (!dpx && !dpy) return;
    e.preventDefault();
    const res = applyMove(panel, selectedId, dpx, dpy, false);
    if (res) updatePanel(panel.id, { [res.slotKey]: res.newArr } as Partial<PanelData>);
  }

  // ── Selection bounds in SVG space ──────────────────────────────────────────
  function getSelBounds(eid: string | null): SelBounds | null {
    if (!eid) return null;
    if (eid === 'panel-body') return { svgX: panLeft, svgY: panTop, svgW: PW * scale, svgH: PH * scale };
    const m = eid.match(/^(\w+var)-(\d+)$/);
    if (!m) return null;
    const sk = m[1] as keyof PanelData;
    const idx = parseInt(m[2]);
    const arr = panel[sk] as Array<{ enabled: boolean; data: any }>;
    if (!Array.isArray(arr) || idx >= arr.length || !arr[idx].enabled) return null;
    const d = arr[idx].data;
    if (sk === 'rbvar')  { const r = d.diameter / 2 * scale; return { svgX: sx(d.x)-r, svgY: syE(d.y)-r, svgW: r*2, svgH: r*2 }; }
    if (sk === 'fhvar')  { const x0 = d.xEnd>0?sx(d.xStart):panLeft, x1=d.xEnd>0?sx(d.xEnd):panRight; return { svgX: x0, svgY: sy(d.y+ffeY+d.height), svgW: x1-x0, svgH: d.height*scale }; }
    if (sk === 'fvvar')  { const y0=d.yEnd>0?sy(d.yEnd+ffeY):panTop, y1=d.yEnd>0?sy(d.yStart+ffeY):panBot; return { svgX: sx(d.x-d.width/2), svgY: y0, svgW: d.width*scale, svgH: y1-y0 }; }
    if (sk === 'plvar')  { const pw=d.width*scale, xL=d.side==='left'?panLeft-pw:panRight, yT=sy(d.height>0?d.height:PH); return { svgX: xL, svgY: yT, svgW: pw, svgH: panBot-yT }; }
    if (sk === 'drvar')  { return { svgX: sx(d.x-d.width/2), svgY: sy(d.y+ffeY+d.height), svgW: d.width*scale, svgH: d.height*scale }; }
    if (sk === 'wcvar') {
      if (d.sideL && d.sideR) return { svgX: sx(0)-10, svgY: syE(d.y)-10, svgW: PW*scale+20, svgH: 20 };
      if (d.sideL) return { svgX: sx(0)-10, svgY: syE(d.y)-10, svgW: 20, svgH: 20 };
      if (d.sideR) return { svgX: sx(PW)-10, svgY: syE(d.y)-10, svgW: 20, svgH: 20 };
      return { svgX: sx(d.x)-10, svgY: syE(d.y)-10, svgW: 20, svgH: 20 };
    }
    if (sk === 'bpvar' || sk === 'ppvar') { return { svgX: sx(d.x)-10, svgY: syE(d.y)-10, svgW: 20, svgH: 20 }; }
    return { svgX: sx(d.x-d.width/2), svgY: sy(d.y+ffeY+d.height/2), svgW: d.width*scale, svgH: d.height*scale };
  }
  // selBounds for primary selection (used for ghost offset)
  // Ghost offset — move the selection box during a move drag
  const ghostOffX = dragDelta && dragRef.current?.action === 'move' ? dragDelta.dpx * scale : 0;
  const ghostOffY = dragDelta && dragRef.current?.action === 'move' ? -dragDelta.dpy * scale : 0;

  // ── All enabled entity IDs for rubberband hit-testing ──────────────────────
  const allEntityIds = useMemo(() => {
    const ids: string[] = [];
    const slots: (keyof PanelData)[] = ['rovar','wdvar','drvar','dlvar','sbvar','rbvar','fhvar','fvvar','plvar','wcvar','bpvar','ppvar'];
    for (const sk of slots) {
      const arr = panel[sk] as Array<{ enabled: boolean }>;
      if (!Array.isArray(arr)) continue;
      arr.forEach((s, i) => { if (s.enabled) ids.push(`${sk}-${i}`); });
    }
    return ids;
  }, [panel]);

  // ── Rubberband helpers ─────────────────────────────────────────────────────
  /** Rubberband corner coords in SVG drawing space (viewBox-aware) */
  function svgPoint(e: React.MouseEvent): { x: number; y: number } {
    return clientToSvg(e.clientX, e.clientY);
  }

  const finishRubberband = useCallback(() => {
    if (!rbStart || !rbEnd) { setRbStart(null); setRbEnd(null); return; }
    const rx = Math.min(rbStart.x, rbEnd.x);
    const ry = Math.min(rbStart.y, rbEnd.y);
    const rw = Math.abs(rbEnd.x - rbStart.x);
    const rh = Math.abs(rbEnd.y - rbStart.y);
    if (rw < 4 && rh < 4) { clearSelection(); setRbStart(null); setRbEnd(null); return; }
    const hits: string[] = [];
    for (const eid of allEntityIds) {
      const sb = getSelBounds(eid);
      if (sb && rectsOverlap(rx, ry, rw, rh, sb.svgX, sb.svgY, sb.svgW, sb.svgH)) {
        hits.push(eid);
      }
    }
    if (hits.length > 0) setSelection(hits);
    else clearSelection();
    setRbStart(null);
    setRbEnd(null);
  }, [rbStart, rbEnd, allEntityIds, setSelection, clearSelection]);

  // ── Unified SVG mouse handlers ─────────────────────────────────────────────
  function onSvgMouseDown(e: React.MouseEvent<SVGSVGElement>) {
    // Left button only — start rubberband
    if (e.button !== 0) return;
    if (dragRef.current) return;
    const pt = svgPoint(e);
    setRbStart(pt);
    setRbEnd(pt);
  }

  function onSvgMouseMoveUnified(e: React.MouseEvent<SVGSVGElement>) {
    if (dragRef.current) { onSvgMouseMove(e); return; }
    if (rbStart) { setRbEnd(svgPoint(e)); }
  }

  function onSvgMouseUpUnified(_e: React.MouseEvent<SVGSVGElement>) {
    if (dragRef.current) { onSvgMouseUp(); return; }
    if (rbStart) { finishRubberband(); }
  }

  return (
    <div className="drawing-2d-container"
      tabIndex={0} onKeyDown={onKeyDown}
      style={{ outline: 'none' }}>
      <div className="drawing-2d-scroll"
        ref={containerRef}
        onMouseLeave={() => { if (dragRef.current) { dragRef.current = null; setDragDelta(null); } }}>
        <div className="drawing-2d-inner">
          <div className="panel-page-frame" style={{ width: pagePxW * pageZoom, height: pagePxH * pageZoom }}>
          <svg
            ref={svgRef}
            className="panel-svg"
            width="100%"
            height="100%"
            viewBox={`0 0 ${svgW} ${svgH}`}
            preserveAspectRatio="xMidYMid meet"
            style={{ background: 'white', border: '1px solid #bbb', boxShadow: '0 2px 10px rgba(0,0,0,0.15)', cursor: dragDelta ? 'grabbing' : rbStart ? 'crosshair' : undefined, touchAction: 'none', display: 'block' }}
        onMouseDown={onSvgMouseDown}
        onMouseMove={onSvgMouseMoveUnified}
        onMouseUp={onSvgMouseUpUnified}
        onClick={() => { if (!rbStart && !dragRef.current) clearSelection(); }}
      >
        {/* ── Defs: hatch patterns ──────────────────────────────────────── */}
        <defs>
          {/* Diagonal hatch — used inside opening cutouts */}
          <pattern id={HATCH_DIAG} patternUnits="userSpaceOnUse" width="10" height="10">
            <line x1="0"  y1="10" x2="10" y2="0"  stroke="#aaa" strokeWidth="0.8" />
            <line x1="-1" y1="1"  x2="1"  y2="-1" stroke="#aaa" strokeWidth="0.8" />
            <line x1="9"  y1="11" x2="11" y2="9"  stroke="#aaa" strokeWidth="0.8" />
          </pattern>
          {/* Horizontal hatch — used for feature strips */}
          <pattern id={HATCH_HORIZ} patternUnits="userSpaceOnUse" width="14" height="5">
            <line x1="0" y1="2.5" x2="14" y2="2.5" stroke="#d4a830" strokeWidth="0.7" />
          </pattern>
        </defs>

        {/* ── Panel concrete body ────────────────────────────────────────── */}
        <path d={outlinePath}
          fill="#cec6b4" />        {/* Panel body — below all features/openings in z-order */}
        <path d={outlinePath}
          fill="transparent" style={{ cursor: 'pointer' }}
          onMouseDown={() => clearSelection()}
          onDoubleClick={onPanelBodyDblClick}
          onContextMenu={onPanelBodyContextMenu}
          onTouchStart={onPanelBodyTouchStart}
          onTouchMove={onPanelBodyTouchMove}
          onTouchEnd={onPanelBodyTouchEnd}
          onClick={e => e.stopPropagation()} />
        {/* ── Horizontal feature strips (fhvar) ─────────────────────────── */}
        {/* ── Horizontal feature strips (fhvar) ── front face only ──────── */}
        {faceFilter !== 'back' && layerVisibility.recess && panel.fhvar.map((s, i) => {
          if (!s.enabled || !s.data.height) return null;
          const d  = s.data;
          const x0 = d.xEnd > 0 ? sx(d.xStart) : panLeft;
          const x1 = d.xEnd > 0 ? sx(d.xEnd)   : panRight;
          const y0 = sy(d.y + ffeY + d.height);
          const h  = d.height * scale;
          return (
            <g key={`fh-${i}`}>
              <rect x={x0} y={y0} width={x1 - x0} height={h} fill="#c8a520" />
              <rect x={x0} y={y0} width={x1 - x0} height={h} fill={`url(#${HATCH_HORIZ})`} opacity="0.7" />
              <rect x={x0} y={y0} width={x1 - x0} height={h} fill="none" stroke="#8a7210" strokeWidth="0.7" />
              {h > 12 && d.mark && (
                <text x={(x0 + x1) / 2} y={y0 + h / 2 + 3} textAnchor="middle"
                  fontSize="7" fontFamily={FONT_TAG} fill="#555">{d.mark}</text>
              )}
              <rect x={x0} y={y0} width={x1 - x0} height={h}
                fill="transparent" style={{ cursor: 'move' }}
                onMouseDown={e => onEntityMouseDown(e, `fhvar-${i}`)}
                onDoubleClick={e => onEntityDblClick(e, `fhvar-${i}`)}
                onContextMenu={e => onEntityContextMenu(e, `fhvar-${i}`)}
                onClick={e => e.stopPropagation()} />
            </g>
          );
        })}

        {/* ── Vertical feature strips (fvvar) ── front face only ────── */}
        {faceFilter !== 'back' && layerVisibility.recess && panel.fvvar.map((s, i) => {
          if (!s.enabled || !s.data.width) return null;
          const d  = s.data;
          const xL = sx(d.x - d.width / 2);
          const w  = d.width * scale;
          const y0 = d.yEnd > 0 ? sy(d.yEnd + ffeY)   : panTop;
          const y1 = d.yEnd > 0 ? sy(d.yStart + ffeY) : panBot;
          return (
            <g key={`fv-${i}`}>
              <rect x={xL} y={y0} width={w} height={y1 - y0} fill="#c8a520" />
              <rect x={xL} y={y0} width={w} height={y1 - y0} fill={`url(#${HATCH_HORIZ})`} opacity="0.7" />
              <rect x={xL} y={y0} width={w} height={y1 - y0} fill="none" stroke="#8a7210" strokeWidth="0.7" />
              <rect x={xL} y={y0} width={w} height={y1 - y0}
                fill="transparent" style={{ cursor: 'move' }}
                onMouseDown={e => onEntityMouseDown(e, `fvvar-${i}`)}
                onDoubleClick={e => onEntityDblClick(e, `fvvar-${i}`)}
                onContextMenu={e => onEntityContextMenu(e, `fvvar-${i}`)}
                onClick={e => e.stopPropagation()} />
            </g>
          );
        })}

        {/* ── Pilasters (plvar) ─────────────────────────────────────────── */}
        {layerVisibility.proud && panel.plvar.map((s, i) => {
          if (!s.enabled || !s.data.width) return null;
          const d  = s.data;
          const pw = d.width * scale;
          // Left pilaster hangs to the LEFT of the panel; right to the RIGHT
          const xL = d.side === 'left' ? panLeft - pw : panRight;
          const yT = sy(d.height > 0 ? d.height : PH);
          const yB = panBot;
          return (
            <g key={`pl-${i}`}>
              <rect x={xL} y={yT} width={pw} height={yB - yT}
                fill="#bab2a0" stroke="#777" strokeWidth="1" strokeDasharray="5,3" />
              {d.mark && (
                <text x={xL + pw / 2} y={(yT + yB) / 2 + 3} textAnchor="middle"
                  fontSize="7" fontFamily={FONT_TAG} fill="#555">{d.mark}</text>
              )}
              <rect x={xL} y={yT} width={pw} height={yB - yT}
                fill="transparent" style={{ cursor: 'move' }}
                onMouseDown={e => onEntityMouseDown(e, `plvar-${i}`)}
                onDoubleClick={e => onEntityDblClick(e, `plvar-${i}`)}
                onContextMenu={e => onEntityContextMenu(e, `plvar-${i}`)}
                onClick={e => e.stopPropagation()} />
            </g>
          );
        })}

        {/* ── Rectangular opening cutouts ────────────────────────────────── */}
        {layerVisibility.void && rects.map((o, i) => {
          const oxL = sx(o.x - o.w / 2);
          const oyT = sy(o.y + o.h / 2);
          const ow  = o.w * scale;
          const oh  = o.h * scale;
          const tag = o.mark ? `(${o.mark})` : `(${o.label})`;
          const isClashing = clashEntityIds.has(o.entityId);
          return (
            <g key={`ro-${i}`}>
              {/* Background fill — red if clashing, white if normal */}
              <rect x={oxL} y={oyT} width={ow} height={oh} fill={isClashing ? '#ff0000' : 'white'} opacity={isClashing ? 0.25 : 1} />
              {/* Diagonal hatch */}
              <rect x={oxL} y={oyT} width={ow} height={oh}
                fill={`url(#${HATCH_DIAG})`} />
              {/* Bold outline — bright red if clashing */}
              <rect x={oxL} y={oyT} width={ow} height={oh}
                fill="none" stroke={isClashing ? '#ff0000' : '#333'} strokeWidth={isClashing ? 2.5 : 1.5} />
              {/* Mark tag */}
              {oh > 16 && (
                <text x={oxL + ow / 2} y={oyT + oh / 2 - (oh > 28 ? 7 : 2)}
                  textAnchor="middle" fontSize="9" fontFamily={FONT_TAG}
                  fontWeight="bold" fill={isClashing ? '#cc0000' : '#1a1a1a'}>{tag}
                </text>
              )}
              {/* W×H label */}
              {oh > 32 && (
                <text x={oxL + ow / 2} y={oyT + oh / 2 + 9}
                  textAnchor="middle" fontSize="7.5" fontFamily={FONT_TAG} fill={isClashing ? '#cc0000' : '#444'}>
                  {toFtIn(o.w)} × {toFtIn(o.h)}
                </text>
              )}
              {/* Hit area */}
              <rect x={oxL} y={oyT} width={ow} height={oh}
                fill="transparent" style={{ cursor: 'move' }}
                onMouseDown={e => onEntityMouseDown(e, o.entityId)}
                onDoubleClick={e => onEntityDblClick(e, o.entityId)}
                onContextMenu={e => onEntityContextMenu(e, o.entityId)}
                onClick={e => e.stopPropagation()} />
            </g>
          );
        })}

        {/* ── Round blockout cutouts ─────────────────────────────────────── */}
        {layerVisibility.void && circs.map((o, i) => {
          const isClashing = clashEntityIds.has(o.entityId);
          return (
            <g key={`rb-${i}`}>
              <circle cx={sx(o.x)} cy={sy(o.y)} r={o.r * scale}
                fill={isClashing ? '#ff0000' : 'white'} opacity={isClashing ? 0.25 : 1}
                stroke={isClashing ? '#ff0000' : '#333'} strokeWidth={isClashing ? 2.5 : 1.3} />
              <circle cx={sx(o.x)} cy={sy(o.y)} r={o.r * scale}
                fill={`url(#${HATCH_DIAG})`} />
              <text x={sx(o.x)} y={sy(o.y) + 4} textAnchor="middle"
                fontSize="7" fontFamily={FONT_TAG} fill={isClashing ? '#cc0000' : '#333'}>
                {o.mark || 'RB'} Ø{toFtIn(o.r * 2)}
              </text>
              {/* Hit area */}
              <circle cx={sx(o.x)} cy={sy(o.y)} r={o.r * scale}
                fill="transparent" style={{ cursor: 'move' }}
                onMouseDown={e => onEntityMouseDown(e, o.entityId)}
                onDoubleClick={e => onEntityDblClick(e, o.entityId)}
                onContextMenu={e => onEntityContextMenu(e, o.entityId)}
                onClick={e => e.stopPropagation()} />
            </g>
          );
        })}

        {/* ── Panel outline (drawn on top to clean up edges) ─────────────── */}
        <path d={outlinePath}
          fill="none" stroke="#111" strokeWidth="2.8" />

        {/* ── Top Plate label at panel top ──────────────────────────────── */}
        <text x={panLeft - 8} y={panTop + 4}
          textAnchor="end" fontSize="8" fontFamily={FONT_TAG} fontWeight="bold" fill="#0d2b4e">T.P.</text>

        {/* ── TOC marker 1½″ below panel top ──────────────────────────── */}
        {PH > 1.5 && (
          <g>
            <line x1={panLeft - 20} y1={sy(PH - 1.5)} x2={panLeft + 30} y2={sy(PH - 1.5)}
              stroke="#2e7d32" strokeWidth="0.7" strokeDasharray="4,3" />
            <text x={panLeft - 24} y={sy(PH - 1.5) + 4}
              textAnchor="end" fontSize="7" fontFamily={FONT_TAG} fill="#2e7d32">TOC</text>
          </g>
        )}

        {/* ── FFE origin — axis crosshair at (left edge, FFE height) ── */}
        {ffeY >= 0 && ffeY <= PH && (
          <g>
            {/* Horizontal FFE datum line */}
            <line x1={panLeft - 20} y1={sy(ffeY)} x2={panRight + 20} y2={sy(ffeY)}
              stroke="#c62828" strokeWidth="1" strokeDasharray="8,4" />
            {/* Vertical origin line at left edge */}
            <line x1={panLeft} y1={sy(ffeY) - 18} x2={panLeft} y2={sy(ffeY) + 18}
              stroke="#c62828" strokeWidth="1.2" />
            {/* Short horizontal tick at origin */}
            <line x1={panLeft - 6} y1={sy(ffeY)} x2={panLeft + 6} y2={sy(ffeY)}
              stroke="#c62828" strokeWidth="1.2" />
            <text x={panLeft - 24} y={sy(ffeY) + 4}
              textAnchor="end" fontSize="8" fontFamily={FONT_TAG} fontWeight="bold" fill="#c62828">FFE</text>
            <text x={panLeft - 24} y={sy(ffeY) + 14}
              textAnchor="end" fontSize="6.5" fontFamily={FONT_TAG} fill="#c62828">(0,0)</text>
          </g>
        )}

        {/* ── Roof line (dashed) ────────────────────────────────────────── */}
        {roofLineY !== null && (
          <g>
            {/* Main roof line — sloped if roofLineYR differs */}
            <line x1={panLeft} y1={sy(roofLineY)}
              x2={panRight} y2={sy(roofLineYR ?? roofLineY)}
              stroke="#555" strokeWidth="1.3" strokeDasharray="14,5" />
            {/* Extension line right of panel */}
            <line x1={panRight} y1={sy(roofLineYR ?? roofLineY)} x2={panRight + 55} y2={sy(roofLineYR ?? roofLineY)}
              stroke="#888" strokeWidth="0.6" strokeDasharray="2,3" />
            <text x={panRight + 8} y={sy(roofLineYR ?? roofLineY) - 3}
              fontSize="8" fontFamily={FONT_TAG} fill="#444">
              {roofLineYR !== null ? 'Roof Line R' : 'Roof Line'}
            </text>
            <text x={panRight + 8} y={sy(roofLineYR ?? roofLineY) + 9}
              fontSize="7.5" fontFamily={FONT_TAG} fill="#777">{toFtIn(roofLineYR ?? roofLineY)}</text>
            {/* Left-side label when sloped */}
            {roofLineYR !== null && (
              <>
                <line x1={panLeft - 55} y1={sy(roofLineY)} x2={panLeft} y2={sy(roofLineY)}
                  stroke="#888" strokeWidth="0.6" strokeDasharray="2,3" />
                <text x={panLeft - 8} y={sy(roofLineY) - 3}
                  textAnchor="end" fontSize="8" fontFamily={FONT_TAG} fill="#444">Roof Line L</text>
                <text x={panLeft - 8} y={sy(roofLineY) + 9}
                  textAnchor="end" fontSize="7.5" fontFamily={FONT_TAG} fill="#777">{toFtIn(roofLineY)}</text>
              </>
            )}
            {/* Delta to top plate */}
            {(PH - (roofLineYR ?? roofLineY)) > 0.1 && (
              <text x={panRight + 8} y={sy(roofLineYR ?? roofLineY) + 20}
                fontSize="7" fontFamily={FONT_TAG} fill="#aaa">
                Δ{toFtIn(PH - (roofLineYR ?? roofLineY))} to T.P.
              </text>
            )}
          </g>
        )}

        {/* ── Top plate ────────────────────────────────────────────────────── */}
        {layerVisibility.proud && panel.tpvar.enabled && (
          <g>
            <rect x={panLeft} y={panTop - panel.tpvar.thickness * scale}
              width={PW * scale} height={panel.tpvar.thickness * scale}
              fill="#777" stroke="#333" strokeWidth="1" />
            <text x={panLeft + PW * scale / 2} y={panTop - panel.tpvar.thickness * scale - 5}
              textAnchor="middle" fontSize="8" fontFamily={FONT_TAG} fill="#333">
              T.P. {panel.tpvar.width}″ {panel.tpvar.material}
            </text>
          </g>
        )}

        {/* ── Weld connections ── face-aware + mount-type filter ──────── */}
        {layerVisibility.embed && panel.wcvar.map((s, i) => {
          if (!s.enabled) return null;
          const wcFace: EntityFace = s.data.face === 'front' ? 'front' : s.data.face === 'back' ? 'back' : 'both';
          if (!isVisibleForFilter(wcFace, faceFilter)) return null;
          return (
            <g key={`wc-${i}`}
              onMouseDown={e => onEntityMouseDown(e, `wcvar-${i}`)}
              onDoubleClick={e => onEntityDblClick(e, `wcvar-${i}`)}
              onContextMenu={e => onEntityContextMenu(e, `wcvar-${i}`)}
              onClick={e => e.stopPropagation()}
              style={{ cursor: 'move' }}>
              <WeldSymbol sx={sx(s.data.x)} sy={syE(s.data.y)} mark={s.data.mark} />
            </g>
          );
        })}

        {/* ── Brace points ── back face + embed layer ───────────────── */}
        {faceFilter !== 'front' && layerVisibility.embed && panel.bpvar.map((s, i) =>
          s.enabled ? (
            <g key={`bp-${i}`}
              onMouseDown={e => onEntityMouseDown(e, `bpvar-${i}`)}
              onDoubleClick={e => onEntityDblClick(e, `bpvar-${i}`)}
              onContextMenu={e => onEntityContextMenu(e, `bpvar-${i}`)}
              onClick={e => e.stopPropagation()}
              style={{ cursor: 'move' }}>
              <BraceSymbol sx={sx(s.data.x)} sy={syE(s.data.y)} mark={s.data.mark} />
            </g>
          ) : null
        )}

        {/* ── Pick points ── back face only ─────────────────────────────── */}
        {faceFilter !== 'front' && panel.ppvar.map((s, i) =>
          s.enabled ? (
            <g key={`pp-${i}`}
              onMouseDown={e => onEntityMouseDown(e, `ppvar-${i}`)}
              onDoubleClick={e => onEntityDblClick(e, `ppvar-${i}`)}
              onContextMenu={e => onEntityContextMenu(e, `ppvar-${i}`)}
              onClick={e => e.stopPropagation()}
              style={{ cursor: 'move' }}>
              <PickSymbol sx={sx(s.data.x)} sy={syE(s.data.y)} mark={s.data.mark} />
            </g>
          ) : null
        )}

        {/* ── CG marker ─────────────────────────────────────────────────────── */}
        <CGMarker
          cx={sx(cgX)} cy={sy(cgY)}
          label={`CG  X:${toFtIn(cgX)}  Y:${toFtIn(cgY - ffeY)}`}
        />

        {/* ── Panel mark — bottom-left corner of drawing ────────────────────── */}
        <text x={panLeft + 5} y={panBot + 20}
          fontSize="15" fontFamily={FONT_TAG} fontWeight="bold" fill="#1a1a1a">
          {mp.panelMark}
        </text>
        <text x={panLeft + 5} y={panBot + 34}
          fontSize="8.5" fontFamily={FONT_TAG} fill="#555">
          {toFtIn(PW)} W × {toFtIn(PH)} H × {mp.thickness}″ THK
        </text>
        {mp.quantity > 1 && (
          <text x={panLeft + 5} y={panBot + 46}
            fontSize="8" fontFamily={FONT_TAG} fill="#777">QTY: {mp.quantity}</text>
        )}

        {/* ── HORIZONTAL DIM CHAIN ──────────────────────────────────────────── */}

        {/* Extension lines from panel top to outermost dim row */}
        <line x1={panLeft}  y1={panTop} x2={panLeft}  y2={dimRow2 - 5}
          stroke="#999" strokeWidth="0.6" strokeDasharray="2,3" />
        <line x1={panRight} y1={panTop} x2={panRight} y2={dimRow2 - 5}
          stroke="#999" strokeWidth="0.6" strokeDasharray="2,3" />

        {/* Extension lines for intermediate opening edges */}
        {horizBreaks.slice(1, -1).map((xb, i) => (
          <line key={`hext-${i}`}
            x1={sx(xb)} y1={panTop} x2={sx(xb)} y2={dimRow1 - 5}
            stroke="#999" strokeWidth="0.6" strokeDasharray="2,3" />
        ))}

        {/* Row 1: individual segment widths */}
        {horizBreaks.slice(0, -1).map((xb, i) => {
          const xn = horizBreaks[i + 1];
          return (
            <HorizDim key={`hd1-${i}`}
              x1={sx(xb)} x2={sx(xn)} y={dimRow1}
              label={toFtIn(xn - xb)} />
          );
        })}

        {/* Row 2: overall width */}
        <HorizDim x1={panLeft} x2={panRight} y={dimRow2} label={toFtIn(PW)} />

        {/* ── VERTICAL DIM CHAIN ────────────────────────────────────────────── */}

        {/* Extension lines from panel right to outermost dim col (overall height) */}
        <line x1={panRight} y1={panBot}  x2={(roofLineY !== null ? dimCol3 : dimCol2) + 5} y2={panBot}
          stroke="#999" strokeWidth="0.6" strokeDasharray="2,3" />
        <line x1={panRight} y1={panTop}  x2={(roofLineY !== null ? dimCol3 : dimCol2) + 5} y2={panTop}
          stroke="#999" strokeWidth="0.6" strokeDasharray="2,3" />

        {/* Extension lines for intermediate opening edges */}
        {vertBreaks.slice(1, -1).map((yb, i) => {
          const isRoofLine = roofLineY !== null && yb === roofLineY;
          if (isRoofLine) return null; // roof line handled separately
          return (
            <line key={`vext-${i}`}
              x1={panRight} y1={sy(yb)} x2={dimCol1 + 5} y2={sy(yb)}
              stroke="#999" strokeWidth="0.6" strokeDasharray="2,3" />
          );
        })}

        {/* Row 1: individual segment heights (excluding roof line breaks) */}
        {vertBreaks
          .filter(yb => roofLineY === null || yb !== roofLineY)
          .slice(0, -1)
          .map((yb, i, arr) => {
            const yn = arr[i + 1];
            if (yn === undefined) return null;
            return (
              <VertDim key={`vd1-${i}`}
                x={dimCol1}
                y1={sy(yn)} y2={sy(yb)}
                label={toFtIn(yn - yb)} />
            );
          })
        }

        {/* Overall height — always outermost vertical dim */}
        <VertDim x={roofLineY !== null ? dimCol3 : dimCol2} y1={panTop} y2={panBot} label={toFtIn(PH)} />

        {/* Roof line height — middle col when present, overall height is outermost */}
        {roofLineY !== null && (
          <>
            {/* extension lines to col 2 */}
            <line x1={panRight} y1={sy(roofLineYR ?? roofLineY)} x2={dimCol2 + 5} y2={sy(roofLineYR ?? roofLineY)}
              stroke="#999" strokeWidth="0.6" strokeDasharray="2,3" />
            <line x1={panRight} y1={panBot} x2={dimCol2 + 5} y2={panBot}
              stroke="#999" strokeWidth="0.6" strokeDasharray="2,3" />
            <VertDim
              x={dimCol2}
              y1={sy(roofLineYR ?? roofLineY)} y2={panBot}
              label={toFtIn(roofLineYR ?? roofLineY)} />
            <text x={dimCol2 + 22} y={sy(roofLineYR ?? roofLineY) - 8}
              fontSize="7.5" fontFamily={FONT_TAG} fill="#666">{roofLineYR !== null ? 'RL R' : 'RL'}</text>
          </>
        )}

        {/* ── Clickable dim overlays → focus PropsPanel ─────────────────────── */}
        {(() => {
          const cx = (panLeft + panRight) / 2;
          const hcy = (panTop + panBot) / 2;
          const rlCy = roofLineY !== null ? (sy(roofLineY) + panBot) / 2 : 0;
          return (
            <>
              {/* Overall width — click to focus width field in PropsPanel */}
              <rect
                x={cx - 36} y={dimRow2 - 20} width={72} height={14}
                fill="transparent" rx={2} style={{ cursor: 'pointer' }}
                onClick={e => { e.stopPropagation(); setFocusField('width'); }}
              />

              {/* Overall height — click to focus height field (outermost col) */}
              <rect
                x={(roofLineY !== null ? dimCol3 : dimCol2) + 3} y={hcy - 36} width={22} height={72}
                fill="transparent" rx={2} style={{ cursor: 'pointer' }}
                onClick={e => { e.stopPropagation(); setFocusField('height'); }}
              />

              {/* Roof line — click to focus roofLine field (middle col) */}
              {roofLineY !== null && (
                <rect
                  x={dimCol2 + 3} y={rlCy - 36} width={22} height={72}
                  fill="transparent" rx={2} style={{ cursor: 'pointer' }}
                  onClick={e => { e.stopPropagation(); setFocusField('roofLine'); }}
                />
              )}
            </>
          );
        })()}

        {/* ── Selection overlay (multi-entity) ─────────────────────────── */}
        {selectedEntities.map(eid => {
          const sb = getSelBounds(eid);
          if (!sb) return null;
          const isPrimary = eid === selectedId;
          const gx = isPrimary ? ghostOffX : 0;
          const gy = isPrimary ? ghostOffY : 0;
          return (
            <g key={`sel-${eid}`}>
              <rect
                x={sb.svgX + gx - 3}
                y={sb.svgY + gy - 3}
                width={sb.svgW + 6}
                height={sb.svgH + 6}
                fill="none" stroke="#0066ff" strokeWidth="1.5" strokeDasharray="5,3"
                pointerEvents="none"
              />
              {/* Resize handles — only on primary, only when not mid-drag */}
              {isPrimary && !dragDelta && getResizeHandles(eid, sb).map(h => (
                <rect key={h.id}
                  x={h.x - HANDLE_SZ / 2} y={h.y - HANDLE_SZ / 2}
                  width={HANDLE_SZ} height={HANDLE_SZ}
                  fill="white" stroke="#0066ff" strokeWidth="1.3"
                  style={{ cursor: h.cursor }}
                  onMouseDown={e => { e.stopPropagation(); onHandleMouseDown(e, eid, h.handle); }}
                />
              ))}
            </g>
          );
        })}

        {/* ── Clash overlay — bright red edge lines + badge ────────────── */}
        {panelClashes.length > 0 && (
          <g pointerEvents="none">
            {/* Bright red lines on panel edges where features overhang */}
            {clashEdges.has('left') && (
              <line x1={panLeft} y1={panTop} x2={panLeft} y2={panBot}
                stroke="#ff0000" strokeWidth="4" />
            )}
            {clashEdges.has('right') && (
              <line x1={panRight} y1={panTop} x2={panRight} y2={panBot}
                stroke="#ff0000" strokeWidth="4" />
            )}
            {clashEdges.has('top') && (
              <line x1={panLeft} y1={panTop} x2={panRight} y2={panTop}
                stroke="#ff0000" strokeWidth="4" />
            )}
            {clashEdges.has('bottom') && (
              <line x1={panLeft} y1={panBot} x2={panRight} y2={panBot}
                stroke="#ff0000" strokeWidth="4" />
            )}
            {/* Clash count badge */}
            <g transform={`translate(${panRight + 12}, ${panTop + 8})`}>
              <circle r="12" fill="#cc0000" />
              <text textAnchor="middle" dy="4.5" fill="white" fontSize="11" fontWeight="bold">{panelClashes.length}</text>
            </g>
          </g>
        )}

        {/* ── Joint gap indicators — foam strips OUTSIDE panel body (like pilasters) ── */}
        {jointTrims.leftTrim > 0 && (() => {
          const tw = Math.max(jointTrims.leftTrim * scale, 6); // min 6px visible width
          return (
            <g pointerEvents="none">
              <rect x={panLeft - tw} y={panTop} width={tw} height={panBot - panTop}
                fill="rgba(255,140,0,0.15)" stroke="#cc6600" strokeWidth="1" strokeDasharray="6 3" />
              <line x1={panLeft} y1={panTop - 4} x2={panLeft} y2={panBot + 4}
                stroke="#cc6600" strokeWidth="1.5" strokeDasharray="4 2" />
              <text x={panLeft - tw - 3} y={panBot + 16} textAnchor="end"
                fontSize="10" fontWeight="bold" fill="#cc6600">{toFtIn(jointTrims.leftTrim)} JG ➡</text>
            </g>
          );
        })()}
        {jointTrims.rightTrim > 0 && (() => {
          const tw = Math.max(jointTrims.rightTrim * scale, 6);
          return (
            <g pointerEvents="none">
              <rect x={panRight} y={panTop} width={tw} height={panBot - panTop}
                fill="rgba(255,140,0,0.15)" stroke="#cc6600" strokeWidth="1" strokeDasharray="6 3" />
              <line x1={panRight} y1={panTop - 4} x2={panRight} y2={panBot + 4}
                stroke="#cc6600" strokeWidth="1.5" strokeDasharray="4 2" />
              <text x={panRight + tw + 3} y={panBot + 16} textAnchor="start"
                fontSize="10" fontWeight="bold" fill="#cc6600">⬅ {toFtIn(jointTrims.rightTrim)} JG</text>
            </g>
          );
        })()}

        {/* ── Rubberband overlay ────────────────────────────────────────── */}
        {rbStart && rbEnd && (
          <rect
            x={Math.min(rbStart.x, rbEnd.x)}
            y={Math.min(rbStart.y, rbEnd.y)}
            width={Math.abs(rbEnd.x - rbStart.x)}
            height={Math.abs(rbEnd.y - rbStart.y)}
            fill="rgba(0,102,255,0.08)" stroke="#0066ff" strokeWidth="1" strokeDasharray="4,3"
            pointerEvents="none"
          />
        )}

        {/* ── Title block table ───────────────────────────────────────────── */}
        {project && project.settings.titleBlock !== 'none' && (() => {
          const mat = calcMaterials(panel);
          const tbTop = panBot + 16;  // 16px gap below panel
          const tbW   = panRight - panLeft;
          const col1W = tbW * 0.30;
          const col2W = tbW * 0.30;
          // col3W = tbW - col1W - col2W
          const col1X = panLeft;
          const col2X = panLeft + col1W;
          const col3X = panLeft + col1W + col2W;
          const headerH = 20;
          const rowH    = 14;
          const dataRows = 7;
          const tbH   = headerH + rowH * dataRows;
          const TBF   = "'Courier New',monospace";
          const TBFS  = 8.5;
          const TBFSL = 9;

          // Project-level data
          const projName  = mp.projectName || project.name || '—';
          const bldgName  = project.buildingName;
          const titleRow  = bldgName ? `${projName} (${bldgName})` : projName;
          const address   = project.address || '—';
          const cityState = [project.city, project.state].filter(Boolean).join(', ') || '—';
          const clientStr = project.client || '—';
          const drawnBy   = mp.drawBy || '—';
          const castLabel = (mp.castingOrientation ?? 'face-down') === 'face-down' ? '☑ OUT (Face Down)' : '☑ IN (Face Up)';
          const dwgNum    = mp.drawingNumber || '—';
          const pMark     = mp.panelMark || '—';
          const dateStr   = mp.date || '—';
          const scaleStr  = mp.drawingScale || computeArchScale(mp.width, mp.height, project.settings.paperSize);

          // Calculated data
          const d1 = toFtIn(mat.diagonal1In) + ' D.F.';
          const d2 = mat.diagonalsMatch ? 'SAME' : toFtIn(mat.diagonal2In) + ' D.F.';
          const cgStr = `X=${toFtIn(mat.cgX)}  Y=${toFtIn(mat.cgY)}`;
          const faceStr = `${mat.exteriorFaceAreaSqFt.toFixed(1)} sf`;
          const finStr  = `${mat.finishedAreaSqFt.toFixed(1)} sf`;
          const thickStr = toFtIn(mp.thickness);
          const volStr   = `${mat.concreteCuYd.toFixed(3)} yd³`;
          const wtStr    = `${mat.panelWeightKips.toFixed(1)} kips`;

          const textY = (row: number) => tbTop + headerH + row * rowH + rowH * 0.72;

          return (
            <g className="title-block" pointerEvents="none">
              {/* Outer border */}
              <rect x={panLeft} y={tbTop} width={tbW} height={tbH}
                fill="#fff" stroke="#111" strokeWidth="1.5" />

              {/* Header row — full width, dark background */}
              <rect x={panLeft} y={tbTop} width={tbW} height={headerH}
                fill="#0e203c" />
              <text x={panLeft + 8} y={tbTop + 14}
                fontSize={TBFSL + 1} fontFamily={TBF} fill="#fff" fontWeight="bold">
                {titleRow}
              </text>
              <text x={panRight - 8} y={tbTop + 14}
                fontSize={TBFSL + 1} fontFamily={TBF} fill="#fff" fontWeight="bold"
                textAnchor="end">
                {pMark}
              </text>

              {/* Column dividers */}
              <line x1={col2X} y1={tbTop + headerH} x2={col2X} y2={tbTop + tbH}
                stroke="#111" strokeWidth="0.8" />
              <line x1={col3X} y1={tbTop + headerH} x2={col3X} y2={tbTop + tbH}
                stroke="#111" strokeWidth="0.8" />
              {/* Header/data divider */}
              <line x1={panLeft} y1={tbTop + headerH} x2={panRight} y2={tbTop + headerH}
                stroke="#111" strokeWidth="0.8" />

              {/* ── Column 1: Project info ── */}
              <text x={col1X + 6} y={textY(0)} fontSize={TBFS} fontFamily={TBF} fill="#222">{address}</text>
              <text x={col1X + 6} y={textY(1)} fontSize={TBFS} fontFamily={TBF} fill="#222">{cityState}</text>
              <text x={col1X + 6} y={textY(2)} fontSize={TBFS} fontFamily={TBF} fill="#222">{clientStr}</text>
              <text x={col1X + 6} y={textY(3)} fontSize={TBFS} fontFamily={TBF} fill="#555">
                Drawn: {drawnBy}
              </text>

              {/* ── Column 2: Drawing info ── */}
              <text x={col2X + 6} y={textY(0)} fontSize={TBFS} fontFamily={TBF} fill="#222">
                Casting: {castLabel}
              </text>
              <text x={col2X + 6} y={textY(1)} fontSize={TBFS} fontFamily={TBF} fill="#222">
                DWG#: {dwgNum} / {pMark}
              </text>
              <text x={col2X + 6} y={textY(2)} fontSize={TBFS} fontFamily={TBF} fill="#222">
                Date: {dateStr}
              </text>
              <text x={col2X + 6} y={textY(3)} fontSize={TBFS} fontFamily={TBF} fill="#222">
                Scale: {scaleStr}
              </text>

              {/* ── Column 3: Calculated ── */}
              <text x={col3X + 6} y={textY(0)} fontSize={TBFS} fontFamily={TBF} fill="#222">
                CG: {cgStr}
              </text>
              <text x={col3X + 6} y={textY(1)} fontSize={TBFS} fontFamily={TBF} fill="#222">
                Face: {faceStr}  Fin: {finStr}
              </text>
              <text x={col3X + 6} y={textY(2)} fontSize={TBFS} fontFamily={TBF} fill="#222">
                T={thickStr}  Vol: {volStr}
              </text>
              <text x={col3X + 6} y={textY(3)} fontSize={TBFS} fontFamily={TBF} fill="#222">
                Weight: {wtStr}
              </text>
              <text x={col3X + 6} y={textY(4)} fontSize={TBFS} fontFamily={TBF} fill="#222">
                D1: {d1}
              </text>
              <text x={col3X + 6} y={textY(5)} fontSize={TBFS} fontFamily={TBF} fill="#222">
                D2: {d2}
              </text>
            </g>
          );
        })()}

        {/* ── Drawing border ────────────────────────────────────────────────── */}
        <rect x={4} y={4} width={svgW - 8} height={svgH - 8}
          fill="none" stroke="#ccc" strokeWidth="0.8" />

      </svg>
          </div>{/* end panel-page-frame */}
        </div>{/* end drawing-2d-inner */}
      </div>{/* end drawing-2d-scroll */}

      <div className="drawing-2d-toolrow">
        <div className="face-filter-bar">
          {(['front', 'back', 'both'] as const).map(f => (
            <button key={f}
              className={`ff-btn${faceFilter === f ? ' active' : ''}`}
              onClick={() => setFaceFilter(f)}
              title={f === 'front' ? 'Front face (exterior)' : f === 'back' ? 'Back face (interior)' : 'Both faces'}>
              {f === 'front' ? 'FR' : f === 'back' ? 'BK' : 'BOTH'}
            </button>
          ))}
        </div>
        <div className="layer-filter-bar">
          {MOUNT_TYPES.map(mt => (
            <button key={mt}
              className={`lf-btn${layerVisibility[mt] ? ' active' : ''}`}
              onClick={() => toggleLayerVisibility(mt)}
              title={MOUNT_TYPE_LABELS[mt]}
              style={{ '--mt-color': MOUNT_TYPE_COLORS[mt] } as React.CSSProperties}>
              {MOUNT_TYPE_LABELS[mt]}
            </button>
          ))}
        </div>
        <div className="zoom-toolbar">
          <button className="zoom-btn" onClick={() => zoomStep(1 / 1.25)} title="Zoom in">+</button>
          <button className="zoom-btn" onClick={fitView} title="Fit">⊟</button>
          <button className="zoom-btn" onClick={() => zoomStep(1.25)} title="Zoom out">−</button>
        </div>
      </div>
    </div>
  );
}
